import { useContext, useEffect } from 'react';

import { Box, Button, Icon, ModalTrigger, Paragraph, Text } from '@zillow/constellation-10';
import { IconLockClosedFilled } from '@zillow/constellation-icons';

import { ModularListingContext } from '../../../../../containers/CreateListingPage/context';

import { FreemiumContext } from '../../../../../components/Freemium/context';

import actions from '../../../../../containers/CreateListingPage/actions';

import numberUtils from '../../../../../utils/numberUtils';

import {
  useGetListingInquiries,
  useGetListingPrices,
  useGetPremiumComps,
  useGetPremiumOverview,
} from '../../../../../hooks/queries/freemium';

import { RoutingSelectors } from '../../../../../selectors';
import { useSelector } from '../../../../../selectors/useSelector';

import type { PremiumPricingGuidanceProps } from '../../../../../model/PremiumPricingGuidance';

import {
  barStyles,
  dataPointContainerStyles,
  dataPointStyles,
  iconBackgroundStyles,
  iconContainerStyles,
} from './styles';

const defaultProps = {
  isActiveProductTypePremium: false,
  pricingGuidance: null,
};

type PremiumPriceTipContentProps = {
  isActiveProductTypePremium: boolean;
  pricingGuidance: PremiumPricingGuidanceProps | null;
};

const PremiumPriceTipContent = ({ isActiveProductTypePremium, pricingGuidance }: PremiumPriceTipContentProps) => {
  const {
    freemiumState: {
      filters: { beds, filterType, propertyType },
    },
  } = useContext(FreemiumContext);

  const { reducerDispatch } = useContext(ModularListingContext);

  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));

  const { refetch: refetchGetListingPrices } = useGetListingPrices(
    { listingId, beds, filterType, propertyType },
    {
      enabled: isActiveProductTypePremium,
      keepPreviousData: true,
    },
  );

  const { refetch: refetchGetListingPerformance } = useGetListingInquiries(
    {
      listingId,
      filterType: filterType,
      beds: beds,
      propertyType: propertyType,
    },
    {
      enabled: isActiveProductTypePremium,
      keepPreviousData: true,
    },
  );

  const { refetch: refetchGetComps } = useGetPremiumComps(
    {
      listingId,
      beds: beds,
      filterType: filterType,
      propertyType: propertyType,
    },
    {
      enabled: isActiveProductTypePremium,
      keepPreviousData: true,
    },
  );

  const { refetch: refetchGetPremiumOverview } = useGetPremiumOverview(
    { listingId },
    { enabled: isActiveProductTypePremium },
  );

  const bodyContent = isActiveProductTypePremium
    ? 'See more data for your rental market to ensure your listing is competitively priced.'
    : 'Gain access to deeper insights and analytics to help you price your rental and optimize your listing.';

  useEffect(() => {
    if (listingId && isActiveProductTypePremium) {
      refetchGetPremiumOverview();
      refetchGetListingPrices();
      refetchGetComps();
      refetchGetListingPerformance();
    }
  }, [isActiveProductTypePremium, pricingGuidance?.property?.price]);

  const handleLearnMoreClick = () => {
    actions.updateIsPremiumPurchaseModalOpen({
      reducerDispatch,
      isPremiumPurchaseModalOpen: true,
    });
  };

  const priceToNumber = (price: string) => Number(price.replace(/[^0-9.-]+/g, ''));

  const calculatePricePercent = (price: string, marketMin: string, marketMax: string) => {
    const priceNum = priceToNumber(price);
    const marketMinNum = priceToNumber(marketMin);
    const marketMaxNum = priceToNumber(marketMax);
    return Math.round(((priceNum - marketMinNum) / (marketMaxNum - marketMinNum)) * 100);
  };

  const renderMarketPricingGraphic = () => {
    if (isActiveProductTypePremium && pricingGuidance && pricingGuidance?.marketMedian?.price) {
      const {
        property: { price: listingPrice },
        marketMedian: { price: medianPrice },
        marketMin: { price: marketMinPrice },
        marketMax: { price: marketMaxPrice },
        market25thPercentile: { price: market25thPercentilePrice },
        market75thPercentile: { price: market75thPercentilePrice },
        rentZestimateRangeLow: { price: rentZestimateLowPrice },
        rentZestimateRangeHigh: { price: rentZestimateHighPrice },
      } = pricingGuidance;

      const isPropertyPriceInRange = listingPrice
        ? priceToNumber(listingPrice) >= priceToNumber(marketMinPrice) &&
          priceToNumber(listingPrice) <= priceToNumber(marketMaxPrice)
        : false;

      return (
        <Box css={{ marginBlock: '600', position: 'relative' }}>
          <Box css={{ display: 'flex', justifyContent: 'center', paddingBlock: '400' }}>
            <Text>
              <Text asChild textStyle="body-bold">
                <span>Your rent:</span>
              </Text>
              {listingPrice
                ? ` ${numberUtils.asCurrency(Number(numberUtils.removeDollarSign(listingPrice)))}`
                : ' Not set'}
            </Text>
          </Box>
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(100, 1fr)' }}>
            <Box css={{ ...barStyles, borderColor: 'border.subtle', gridColumnStart: 1, gridColumnEnd: 100 }} />
            {/* for 5500+ price mkts: do not show if these 2 values are equal to avoid weirdness in chart */}
            {market25thPercentilePrice !== market75thPercentilePrice && (
              <Box
                css={{ ...barStyles, borderColor: 'border.accent.brand.hero' }}
                style={{
                  gridColumnStart: calculatePricePercent(market25thPercentilePrice, marketMinPrice, marketMaxPrice),
                  gridColumnEnd: calculatePricePercent(market75thPercentilePrice, marketMinPrice, marketMaxPrice),
                }}
              />
            )}
          </Box>
          {/* only plot listingPrice if it's within the market range */}
          {isPropertyPriceInRange && (
            <Box
              css={dataPointContainerStyles}
              style={{ marginLeft: `${calculatePricePercent(listingPrice, marketMinPrice, marketMaxPrice)}%` }}
            >
              <Box css={dataPointStyles} />
            </Box>
          )}
          <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text color="text.subtle">
              Min: {numberUtils.asCurrency(Number(numberUtils.removeDollarSign(marketMinPrice)))}
            </Text>
            <Text color="text.subtle">
              Max: {numberUtils.asCurrency(Number(numberUtils.removeDollarSign(marketMaxPrice)))}
            </Text>
          </Box>
        </Box>
      );
    }

    //  non premium static preview version
    return (
      <>
        <Box css={{ marginBlock: '600', position: 'relative' }}>
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(100, 1fr)', filter: 'blur(1.5px)' }}>
            <Box css={{ ...barStyles, borderColor: 'border.subtle', gridColumnStart: 1, gridColumnEnd: 100 }} />
            <Box
              css={{ ...barStyles, borderColor: 'border.accent.brand.hero', gridColumnStart: 10, gridColumnEnd: 70 }}
            />
          </Box>
          <Box css={{ display: 'flex', justifyContent: 'space-between', filter: 'blur(1.5px)' }}>
            <Text textStyle="body-sm">Min</Text>
            <Text textStyle="body-sm">Max</Text>
          </Box>
          <Box css={iconContainerStyles}>
            <Box css={iconBackgroundStyles}>
              <Text textStyle="body-sm" css={{ display: 'inline-flex', alignItems: 'center' }} asChild>
                <span>
                  {' '}
                  <Icon
                    render={<IconLockClosedFilled />}
                    css={{
                      color: 'icon.accent.brand.hero',
                      backgroundColor: 'bg.neutral',
                      borderRadius: '50%',
                      display: 'inline-block',
                    }}
                  />{' '}
                </span>
              </Text>
            </Box>
          </Box>
        </Box>
      </>
    );
  };

  return (
    <div data-test-id="PremiumPriceTipContent">
      <Paragraph>{bodyContent}</Paragraph>
      {renderMarketPricingGraphic()}
      <Box>
        {isActiveProductTypePremium && (
          <>
            <ModalTrigger multiModalTriggerId="PremiumPricingGuidanceModal">
              <Button fluid={true}>Explore data</Button>
            </ModalTrigger>
          </>
        )}
        {!isActiveProductTypePremium && (
          <Button tone="brand" emphasis="outlined" fluid onClick={handleLearnMoreClick}>
            Learn more
          </Button>
        )}
      </Box>
    </div>
  );
};

PremiumPriceTipContent.defaultProps = defaultProps;

export default PremiumPriceTipContent;
